import { IGame } from "../types/game";
import { ICanvas } from "../types/canvas";
import { IPlayer } from "../models/player";
import { IState } from "../types/state";

export default abstract class GameState implements IState {
    
    player: IPlayer
    canvas: ICanvas
    game: IGame
    frameTime: number
    private firstTime: boolean = true // Match original first_time flag
    private done: boolean = false // Match original done flag
    private parentExecutor: any = null // Matches original Module::parent

    constructor(game: IGame){
        this.player = game.player
        this.canvas = game.canvas
        this.game = game
    }
    
    /**
     * Set parent executor (matches original Module::parent)
     * Reference: quadra/source/overmind.h:56 (Module::parent)
     */
    setParentExecutor(executor: any): void {
        this.parentExecutor = executor;
    }
    
    /**
     * Get parent executor
     */
    getParentExecutor(): any {
        return this.parentExecutor;
    }
    
    /**
     * Call another state in the same executor (matches original Module::call)
     * Reference: quadra/source/overmind.cc:149-151 (Module::call)
     * This adds the state to the executor stack, pausing this state until the called state returns
     */
    call(state: IState): void {
        // Match original Module::call() - only adds to parent executor
        // Reference: quadra/source/overmind.cc:149-151
        // Original: void Module::call(Module* module) { parent->add(module); }
        if (this.parentExecutor && this.parentExecutor.add) {
            this.parentExecutor.add(state);
        } else {
            // Fallback: if not in an executor, this shouldn't happen in normal flow
            console.error(`[GameState.call] No parent executor! State: ${this.toString()}, trying to call: ${state.toString()}`);
        }
    }

    /**
     * Execute a new state and mark current as done (matches original Module::exec)
     * Reference: quadra/source/overmind.cc:144-147 (Module::exec)
     * Original: void Module::exec(Module* module) { call(module); ret(); }
     * This is equivalent to call() + ret() - adds new state and marks current as done
     */
    exec(state: IState): void {
        this.call(state);
        this.ret();
    }

    enter(): void { }

    update(frameTime: number): void { 
        this.frameTime = frameTime;
        // Mark that we've been updated at least once (matches original first_time = false)
        this.firstTime = false;

        // Track playing time (matches original Player_base::step() line 43-44)
        // Reference: quadra/source/player.cc:43-44
        // Original: if(game && game->net_version()<23 && !game->paused && (canvas->idle==0 || canvas->idle==1))
        //   canvas->stats[CS::PLAYING_TIME].add(1);
        // Note: We track for all versions, not just < 23
        if (this.game && !(this.game as any).paused && (this.canvas.idle === 0 || this.canvas.idle === 1)) {
          if (this.game.stats && this.game.stats["PLAYING_TIME"]) {
            this.game.stats["PLAYING_TIME"].add(1);
          }
        }

        // Calculate PPM and BPM every 128 frames (matches original Player_base::step() lines 45-54)
        // Reference: quadra/source/player.cc:45-54
        // Original: if(!((overmind.framecount+1)&127)) { ... }
        const framecount = (this.game as any).overmind?.framecount || 0;
        if (!((framecount + 1) & 127)) {
          // Calculate PPM (Pieces Per Minute) = (SCORE * 6000) / PLAYING_TIME
          if (this.game.stats && this.game.stats["PLAYING_TIME"] && this.game.stats["PLAYING_TIME"].getValue() > 0) {
            const playingTime = this.game.stats["PLAYING_TIME"].getValue();
            const score = this.game.stats["SCORE"]?.getValue() || 0;
            const ppm = Math.floor((score * 6000.0) / playingTime);
            if (this.game.stats["PPM"]) {
              this.game.stats["PPM"].setValue(ppm);
            }
          }

          // Calculate BPM (Blocks Per Minute) = (COMPTETOT * 6000) / PLAYING_TIME
          if (this.game.stats && this.game.stats["PLAYING_TIME"] && this.game.stats["PLAYING_TIME"].getValue() > 0) {
            const playingTime = this.game.stats["PLAYING_TIME"].getValue();
            const comptetot = this.game.stats["COMPTETOT"]?.getValue() || 0;
            const bpm = Math.floor((comptetot * 6000.0) / playingTime);
            if (this.game.stats["BPM"]) {
              this.game.stats["BPM"].setValue(bpm);
            }
          }
        }
    }

    // Method to mark firstTime as false (for use when enter() is called without update())
    // Made public so Game can call it
    markFirstTimeComplete(): void {
        this.firstTime = false;
    }

    // Check if this is the first time this state is being updated
    isFirstTime(): boolean {
        return this.firstTime;
    }

    // Mark this state as done (matches original ret() which sets done=true)
    markDone(): void {
        this.done = true;
    }

    // Ret() method - matches original Module::ret() which sets done=true
    // Reference: quadra/source/overmind.cc:153-155
    ret(): void {
        this.done = true;
    }

    // Check if this state is done
    isDone(): boolean {
        return this.done;
    }

    moveLeft(): boolean {
        let ret: boolean = false;
        if (!this.canvas.bloc) return ret;
        if (!this.canvas.checkCollide(this.canvas.bloc,
            this.canvas.bloc.x - 1,
            this.canvas.bloc.y,
            this.canvas.bloc.rotation
        )) {
            ret = true;
            this.canvas.bloc.x--;
        }
        this.canvas.setBit(2); // matches original line 198
        return ret;
    }

    moveRight(): boolean {
        let ret: boolean = false;
        if (!this.canvas.bloc) return ret;
        if (!this.canvas.checkCollide(this.canvas.bloc,
            this.canvas.bloc.x + 1,
            this.canvas.bloc.y,
            this.canvas.bloc.rotation
        )) {
            ret = true;
            this.canvas.bloc.x++;
        }
        this.canvas.setBit(4); // matches original line 208
        return ret;
    }

    rotateLeft(): boolean {
        let ret: boolean = false;
        if (!this.canvas.bloc) return ret;
        const targetRotation: number = (this.canvas.bloc.rotation - 1) & 3;
        if (!this.canvas.checkCollide(this.canvas.bloc,
            this.canvas.bloc.x,
            this.canvas.bloc.y,
            targetRotation
        )) {
            ret = true;
            this.canvas.bloc.rotation = targetRotation;
        }
        this.canvas.setBit(8); // matches original line 147
        return ret;
    }

    rotateRight(twice: boolean = false): boolean {
        let ret: boolean = false;
        if (!this.canvas.bloc) return ret;
        const inc: number = twice ? 2 : 1;
        const targetRotation = (this.canvas.bloc.rotation + inc) & 3;
        if (!this.canvas.checkCollide(this.canvas.bloc,
            this.canvas.bloc.x,
            this.canvas.bloc.y,
            targetRotation
        )) {
            ret = true;
            this.canvas.bloc.rotation = targetRotation;
        }
        if (twice) {
            this.canvas.setBit(32); // matches original line 167
        } else {
            this.canvas.setBit(16); // matches original line 169
        }
        return ret;
    }

    moveDown(): void {
        if (!this.canvas.bloc) return;
        const targetY = this.calcBy(this.canvas.bloc.posY + this.canvas.downSpeed);
        if(!this.canvas.checkCollide(this.canvas.bloc, this.canvas.bloc.x, targetY, this.canvas.bloc.rotation )) {
            this.canvas.bloc.posY += this.canvas.downSpeed;
            this.canvas.bloc.y = this.calcBy(this.canvas.bloc.posY);
        } else { // is used to "settle" the blockk so that it drops
            this.canvas.bloc.posY = ((this.canvas.bloc.y-12)*18<<4)-1;
        }
        this.canvas.setBit(1); // matches original line 182
    }

    dropDown(): void {
        if (!this.canvas.bloc) return;
        while(!this.canvas.checkCollide(this.canvas.bloc, this.canvas.bloc.x, this.canvas.bloc.y+1, this.canvas.bloc.rotation))
            this.canvas.bloc.y++;
        this.canvas.bloc.calcPosXY();
        this.canvas.setBit(64); // matches original line 189
    }

    calcBy(posY: number): number {
        return Math.trunc((((posY+15+(12*18<<4))>>4)+17)/18);
    }
}